import BaseMap from 'components/BaseMap/BaseMap';
import PlacesSearch from 'components/Header/PlacesSearch';
import ConfirmationModal from 'components/Modals/ConfirmationModal';
import SearchBar from 'components/SearchBar/SearchBar';
import BaseTable from 'components/Tables/BaseTable';
import { usePerms } from 'contexts/PermsContext';
import usePlaces from 'features/UserMenu/features/Places/hooks/usePlaces';
import useLocation from 'hooks/useLocation';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { Button, Container, Form } from 'react-bootstrap';
import {
  FaBuilding,
  FaBuildingColumns,
  FaCity,
  FaFloppyDisk,
  FaHouse,
  FaMapLocation,
  FaPen,
  FaXmark,
} from 'react-icons/fa6';
import { useSelector } from 'react-redux';
import { useSearchParams } from 'react-router-dom';
import { selectUserSelectedLocationPlaceId } from 'store/selectors/User/userSelectors';

/**
 * The classic Places tab — the page as it was before the Merlin Slate restyle (0c1fb517e).
 * Shown when the person picks Classic navigation in the display settings.
 */
export const ClassicUserPlaces = () => {
  const {
    places,
    loadingPlaces,
    fetchingPlaces,
    addingPlaces,
    selectingPlace,
    fullSelectedPlaceObject,
    selectPlace,
    addPlace,
    removePlace,
    renamePlace,
    changeHomePlace,
  } = usePlaces();
  const userSelectedPlaceId = useSelector(selectUserSelectedLocationPlaceId);
  const { locations } = useLocation();
  const effectiveLocation = locations.effectiveLocation;
  const [mapCenter, setMapCenter] = useState([...effectiveLocation.coords]);
  const [selectedLocation, setSelectedLocation] = useState(null);
  const [editIndex, setEditIndex] = useState(-1);
  const [workingTitle, setWorkingTitle] = useState('');
  const [selectedPlace, setSelectedPlace] = useState(null);
  const [showConfirmDelete, setShowConfirmDelete] = useState(false);
  const [placeToDelete, setPlaceToDelete] = useState(null);
  const perms = usePerms();
  const [params] = useSearchParams();

  function typeMap(type) {
    switch (type) {
      case 'locality':
        return 'City';
      case 'administrative_area_level_1':
        return 'State';
      case 'administrative_area_level_2':
        return 'County';
      case 'premise':
        return 'Building';
      case 'country':
        return 'Country';
      case 'geocode':
        return 'Location';
      default:
        return 'Building';
    }
  }

  function typeIcon(type) {
    switch (type) {
      case 'DefaultLocationUserHome':
        return <FaHouse size={20} color="currentColor" />;
      case 'locality':
        return <FaCity size={20} color="currentColor" />;
      case 'administrative_area_level_1':
        return <FaBuildingColumns size={20} color="currentColor" />;
      case 'administrative_area_level_2':
        return <FaBuildingColumns size={20} color="currentColor" />;
      case 'premise':
        return <FaBuilding size={20} color="currentColor" />;
      case 'geocode':
        return <FaMapLocation size={20} color="currentColor" />;
      default:
        return <FaBuilding size={20} color="currentColor" />;
    }
  }

  useEffect(() => {
    if (loadingPlaces || fetchingPlaces) return;

    if (places.length === 0) {
      setSelectedPlace(null);
      return;
    }

    setSelectedPlace(fullSelectedPlaceObject || places[0]);
  }, [loadingPlaces, fetchingPlaces, places, fullSelectedPlaceObject]);

  const addPreviewLocation = (location) => {
    setMapCenter([location.latitude, location.longitude]);
    setSelectedLocation(location);
  };

  const saveLocation = useCallback(
    async (location) => {
      if (!location?.place_id) return;

      const savedPlace = places.find((place) => place.place_id === location.place_id);

      if (savedPlace) {
        await selectPlace(savedPlace);
        setSelectedPlace(savedPlace);
        return;
      }

      await addPlace(location);
    },
    [addPlace, places, selectPlace],
  );

  const editLocationTitle = useCallback(
    async (location, title) => {
      if (!location?.place_id) return;

      await renamePlace(location.place_id, title);
    },
    [renamePlace],
  );

  const changeHomeLocation = useCallback(
    async (location) => {
      const newHomePlace = await changeHomePlace(location);

      setEditIndex(-1);

      if (newHomePlace) setSelectedPlace(newHomePlace);
    },
    [changeHomePlace],
  );

  useEffect(() => {
    if (selectedPlace) {
      setMapCenter([selectedPlace.latitude, selectedPlace.longitude]);
      setSelectedLocation(selectedPlace);
    }
  }, [selectedPlace]);

  const removeLocation = useCallback(
    async (location) => {
      if (!location?.place_id) return;

      const newPlaces = await removePlace(location.place_id);

      const nextSelectedPlace =
        newPlaces?.find((place) => place.place_id === userSelectedPlaceId) || newPlaces[0] || null;
      setSelectedPlace(nextSelectedPlace);

      // This ensures that if the user deletes the currently selected place, we select the next best place
      if (nextSelectedPlace?.place_id !== userSelectedPlaceId) {
        await selectPlace(nextSelectedPlace);
      }
    },
    [removePlace, userSelectedPlaceId, selectPlace],
  );

  const searchFilter = useCallback(
    (place) => {
      if (!place) return false;

      const search = params.get('search');

      if (search) {
        return (
          place.title
            ?.toLowerCase()
            .replace(/\s/g, '')
            .includes(search?.toLowerCase().replace(/\s/g, '')) ||
          place.state
            ?.toLowerCase()
            .replace(/\s/g, '')
            .includes(search?.toLowerCase().replace(/\s/g, ''))
        );
      } else return true;
    },
    [params],
  );

  const columns = useMemo(() => {
    return [
      {
        header: 'Title',
        accessorKey: 'title',
        Cell: ({ row, cell }) => {
          const title = cell.getValue();
          const typeIcon = row.original.typeIcon;
          const index = row.original.index;
          const place = row.original.place;

          return (
            <div className="d-flex align-items-center gap-2">
              <div className="rounded-circle d-flex justify-content-center align-items-center p-2 border border-2 border-white bg-primary-shadow text-theme">
                {typeIcon}
              </div>
              {editIndex !== index ? (
                <p className="fs-6">{title}</p>
              ) : (
                <Form
                  onSubmit={(e) => {
                    e.preventDefault();
                    editLocationTitle(place, workingTitle);
                    setEditIndex(-1);
                  }}
                >
                  <Form.Control
                    value={workingTitle}
                    onChange={(e) => {
                      setWorkingTitle(e.target.value);
                    }}
                    className="w-100 rounded-2"
                    style={{ border: '1px solid currentColor' }}
                  ></Form.Control>
                </Form>
              )}
            </div>
          );
        },
        size: 180,
      },
      {
        header: 'Type',
        accessorKey: 'type',
        Cell: ({ row, cell }) => {
          const type = cell.getValue();
          return <p className="fs-6">{type}</p>;
        },
        size: 100,
      },
      {
        header: 'Location',
        accessorKey: 'location',
        Cell: ({ row, cell }) => {
          const location = cell.getValue();
          return <p className="fs-6">{location}</p>;
        },
        size: 130,
      },
      {
        header: '',
        id: 'Actions',
        columnDefType: 'display',
        Cell: ({ row, cell }) => {
          const place = row.original.place;
          const index = row.original.index;

          return (
            <div className="cursor-pointer d-flex justify-content-end align-items-center gap-2">
              {perms.testPermission([8, 9, 10, 11, 12, 13], ['Create-EditPlaces']) &&
                editIndex === index && (
                  <div
                    onClick={() => {
                      editLocationTitle(place, workingTitle);
                      setEditIndex(-1);
                    }}
                  >
                    <FaFloppyDisk color="currentColor" />
                  </div>
                )}
              {perms.testPermission([8, 9, 10, 11, 12, 13], ['Create-EditPlaces']) &&
                editIndex !== index && (
                  <div
                    onClick={() => {
                      setEditIndex(index);
                      setWorkingTitle(place.title || '');
                    }}
                  >
                    <FaPen size={15} color="currentColor" />
                  </div>
                )}
              {!place.DefaultLocationUserHome &&
                perms.testPermission([8, 9, 10, 11, 12, 13], ['HomeLocationEditable']) && (
                  <div
                    onClick={(e) => {
                      e.stopPropagation();
                      changeHomeLocation(place);
                    }}
                  >
                    <FaHouse size={15} color="currentColor" />
                  </div>
                )}
              {perms.testPermission([8, 9, 10, 11, 12, 13], ['Create-EditPlaces']) &&
                !place.DefaultLocationUserHome && (
                  <div
                    onClick={() => {
                      setPlaceToDelete(place);
                      setShowConfirmDelete(true);
                    }}
                  >
                    <FaXmark size={15} color="currentColor" />
                  </div>
                )}
            </div>
          );
        },
        enableSorting: false,
        size: 180,
      },
    ];
  }, [changeHomeLocation, editIndex, perms, workingTitle, editLocationTitle]);

  const rows = useMemo(() => {
    return places
      .map((place, index) => ({
        id: place.place_id,
        title: place.title,
        typeIcon: typeIcon(place.type),
        type: typeMap(place.type),
        location: place.place_data?.formatted_address,
        place: place,
        index: index,
      }))
      .filter(searchFilter);
  }, [places, searchFilter]);

  return (
    <Container fluid className="page-scroll-area mt-3">
      {showConfirmDelete && (
        <ConfirmationModal
          show={showConfirmDelete}
          handleShow={() => setShowConfirmDelete(false)}
          bodyTxt={`Are you sure you want to delete this place?`}
          cancelBtnTxt="No"
          confirmBtnTxt="Yes"
          confirmHandler={() => {
            removeLocation(placeToDelete);
            setShowConfirmDelete(false);
          }}
        />
      )}
      <div className="d-flex flex-column flex-xl-row h-100" style={{ gap: 20 }}>
        {perms.testPermission([8, 9, 10, 11, 12, 13], ['Create-EditPlaces']) && (
          <div className="d-flex flex-column rounded-3 p-2 w-100 bg-base h-max">
            <div className="d-flex flex-column flex-md-row justify-content-between mb-2">
              <div className="fw-bold fs-5">Add a Place</div>
              <PlacesSearch
                noBack
                noAutoAdd
                places={places}
                setSearching={() => {}}
                addLocation={addPreviewLocation}
                setMapCenter={setMapCenter}
                quickSave
                cwValidated
              />
            </div>
            {/* Map Container - height is 75% of the width */}
            <div
              className="ratio ratio-4x3 w-100 overflow-hidden rounded-2"
              style={{ background: 'grey' }}
            >
              <BaseMap mapCenter={mapCenter} zoom={11} disableMapReset></BaseMap>
            </div>
            <div className="d-flex flex-column">
              {selectedLocation && (
                <div className="p-1 mt-1">{selectedLocation.place_data?.formatted_address}</div>
              )}
              <Button
                onClick={() => {
                  saveLocation(selectedLocation);
                }}
                disabled={!selectedLocation || addingPlaces || selectingPlace}
                className="mt-2 p-2 cursor-pointer rounded-2 w-max"
                variant="primary"
              >
                Save
              </Button>
            </div>
          </div>
        )}
        <div className="d-flex flex-column w-100 p-2 rounded-3">
          <div className="d-flex justify-content-between flex-column flex-md-row">
            <div className="fw-bold fs-5">Saved Places</div>
            {/* Right Side Search Bar */}
            <SearchBar className="p-2 mb-2 rounded-4 w-auto border-0 bg-primary-hightlight"></SearchBar>
          </div>
          <BaseTable
            columns={columns}
            rows={rows}
            selectedRowId={selectedPlace?.place_id}
            customStyles={{ cell: { borderBottom: '1px solid var(--color-divider)' } }}
            onRowClick={(row) => {
              setSelectedPlace(row.place);
            }}
          />
        </div>
      </div>
    </Container>
  );
};
